import { useState } from 'react'
import {
  useDispatch,
  useSelector
} from 'react-redux'
import {
  Box,
  FocusLock,
  IconButton,
  Stack,
  Text
} from '@chakra-ui/react'
import {
  ArrowIcon,
  SettingsIcon
} from 'assets/icons'
import {
  AnimatePresence,
  motion
} from 'framer-motion'
import {
  setOpenBlogForm,
  setUserSectionPage,
  usersSelectors,
  setExecAutoArchive,
  setOnPostEditor,
  setCloseAllDrawers
} from 'store'
import {
  BLOGS,
  EDIT_PROFILE,
  MY_PROFILES,
  PLATFORM_LIST,
  SETTINGS,
  SINGLE_PROFILE,
  FIRST_TAB_COLOR_INFO,
  SECOND_TAB_COLOR_INFO,
  THIRD_TAB_COLOR_INFO
} from 'utils/constants'
import {
  isAuth,
  isNotMobile
} from 'utils/helpers'
import { COLORS } from 'utils/styles'

/* eslint-disable import/no-cycle */
import { UserSection } from 'components'
import { Auth } from 'components/Auth'
import { CropPicture } from '../CropPicture'
import { DiscardChanges } from '../DiscardChanges'

const MotionBox = motion(Box)

export const UserPanel = ({
  isDiscard,
  setIsDiscard,
  blogFinishAnimation,
  isClickedOutside,
  setIsClickedOutside,
  isBlogForMobile,
  handleCloseButton,
  handleSettingButton
}) => {
  const {
    user,
    isBlogFormOpen,
    isProfileChanged,
    isProfileIncomplete,
    isLocationOpen,
    isPictureUploaded,
    closeResetProfileModal,
    cropFor,
    postsTypeStatus,
    userSectionPage,
    onPostEditor,
    postPostsApiMsg
  } = useSelector(usersSelectors)

  const dispatch = useDispatch()
  const [closeCropModal, setCloseCropModal] = useState(true)
  const [selectedUser, setSelectedUser] = useState('')
  const [socialLinksArray, setSocialLinksArray] = useState([])
  const [socialLinkSelected, setSocialLinkSelected] = useState([])
  let touchStart
  let touchEnd

  const isOnEditProfile = userSectionPage.at(-1) === EDIT_PROFILE
    || userSectionPage.at(-1) === PLATFORM_LIST
  const dontShowSettingsIcon = isBlogForMobile()
    || isBlogFormOpen
    || userSectionPage.at(-1) === EDIT_PROFILE
    || userSectionPage.at(-1) === SETTINGS
    || userSectionPage.at(-1) === PLATFORM_LIST

  const blogHeader = () => {
    if (postPostsApiMsg !== '') {
      return postPostsApiMsg
    }

    if (onPostEditor === 'post') {
      return 'New Blog'
    }

    if (onPostEditor === 'put') {
      return 'Edit Blog'
    }

    if (postsTypeStatus === 'archived') {
      return 'Archived Blogs'
    }

    if (postsTypeStatus === 'scheduled') {
      return 'Scheduled Blogs'
    }

    return 'My Blogs'
  }

  const handleOverlayZIndex = () => {
    if (userSectionPage.at(-1) === EDIT_PROFILE && isProfileChanged) {
      return 1408
    }

    return isAuth() ? 1399 : 1390
  }

  const closingCondition = () => {
    if (
      closeCropModal
      && closeResetProfileModal
    ) {
      if (isOnEditProfile) {
        if (
          isProfileChanged === true
          && isLocationOpen === false
        ) {
          setIsDiscard(true)
        } else if (isLocationOpen === true) {
          setIsDiscard(false)
        } else {
          dispatch(setUserSectionPage('clear'))
        }
      } else if (userSectionPage.at(-1) === SETTINGS) {
        dispatch(setUserSectionPage('clear'))
      } else {
        if (
          userSectionPage.at(-1) === BLOGS
          && onPostEditor === 'post'
        ) {
          dispatch(setExecAutoArchive(true))
          dispatch(setOnPostEditor(''))
        }

        setIsDiscard(false)
        dispatch(setUserSectionPage('clear'))
      }
    }
  }

  const animateVariants = {
    initial: {
      x: -500,
      marginBlock: isBlogForMobile() ? '60px' : '60px',
      marginInline: isBlogForMobile() ? '10px' : '10px',
      width: isBlogForMobile() ? '95%' : '95%',
      borderRadius: isBlogForMobile() ? '10px' : '10px',
      height: isBlogForMobile() ? '100dvh' : 'calc(100dvh - 120px)'
    },
    animate: {
      x: 0,
      marginBlock: isBlogForMobile() ? '0px' : '60px',
      marginInline: isBlogForMobile() ? '0px' : '10px',
      width: isBlogForMobile() ? '100%' : '95%',
      borderRadius: isBlogForMobile() ? '0px' : '10px',
      height: isBlogForMobile() ? '100dvh' : 'calc(100dvh - 120px)',
      transition: {
        type:
          isBlogForMobile()
          || blogFinishAnimation
            ? 'tween'
            : 'spring',
        duration:
          isBlogForMobile()
          || blogFinishAnimation
            ? 1
            : 0.8
      }
    },
    exit: {
      x: -500,
      transition: {
        type: 'tween',
        duration:
          isBlogForMobile()
          || blogFinishAnimation
            ? 1
            : 0.8
      }
    }
  }

  const handleContent = (pageName) => (
    <MotionBox
      id={`${pageName}-panel`}
      key={pageName}
      variants={animateVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      overflow="hidden"
      background={COLORS.gray}
      maxWidth="450px"
      boxShadow="0 4px 10px rgba(0, 0, 0, 0.2) !important"
      position="fixed"
      borderRadius="10px"
      top={0}
      left={0}
      display="flex"
      flexDirection="column"
      zIndex={1412}
    >
      <Stack
        alignItems="center"
        justifyContent="space-between"
        direction="row"
        p="0px"
        background={
          isNotMobile()
            ? COLORS.goldGradientLeftToRight
            : COLORS.goldGradientRightToLeft
        }
        zIndex={5}
        height="48px"
        overflow="hidden"
        position="relative"
        flex="0 0 48px"
        onTouchStart={(e) => {
          touchStart = e?.targetTouches[0].clientX
        }}
        onTouchMove={(e) => {
          touchEnd = e?.targetTouches[0].clientX
        }}
        onTouchEnd={() => {
          if (touchStart - touchEnd > 45) {
            closingCondition()
          }
        }}
      >
        <IconButton
          name="profile-pane-back-btn"
          _hover={{ background: COLORS.gold_hover }}
          background="unset"
          marginLeft="5px"
          icon={<ArrowIcon />}
          onClick={() => {
            if (isBlogFormOpen) {
              dispatch(setOpenBlogForm(false))
            } else if (
              pageName === SINGLE_PROFILE
              && onPostEditor === 'post'
            ) {
              dispatch(setExecAutoArchive(true))
              dispatch(setOnPostEditor(''))
            } else {
              setIsClickedOutside(false)
              handleCloseButton()
            }
          }}
          outline="none"
          boxShadow="none"
          css={{
            ':focus': {
              outline: 'none',
              boxShadow: 'none'
            }
          }}
        />

        {
          pageName === PLATFORM_LIST
          && (
            <>
              <Text
                whiteSpace="nowrap"
                fontSize="20px"
                sx={{
                  marginLeft: '105px !important',
                  '@media (max-width: 480px)': {
                    marginLeft: '65px !important'
                  }
                }}
                fontWeight={600}
              >
                Choose Platform
              </Text>
              <Box width="95%" />
            </>
          )
        }

        {
          pageName === BLOGS && (
            <Text
              whiteSpace="nowrap"
              fontSize="18px"
              fontWeight={500}
              backgroundColor={postPostsApiMsg === '' ? '' : '#FBFCF8'}
              padding={postPostsApiMsg === '' ? '' : '0 10px'}
              borderRadius={postPostsApiMsg === '' ? '' : '5px'}
            >
              { blogHeader() }
            </Text>
          )
        }

        {
          user
          && (
            <IconButton
              _hover={{ background: COLORS.gold_hover }}
              background="unset"
              icon={<SettingsIcon />}
              onClick={handleSettingButton}
              outline="none"
              boxShadow="none"
              opacity={dontShowSettingsIcon ? 0 : 1}
              pointerEvents={dontShowSettingsIcon ? 'none' : 'auto'}
              css={{
                marginRight: '5px',
                ':focus': {
                  outline: 'none',
                  boxShadow: 'none'
                }
              }}
            />
          )
        }
      </Stack>

      <Box
        id={`${pageName}-drawer-body`}
        sx={{
          overflowX: 'hidden',
          '::MsOverflowStyle': 'none',
          scrollbarWidth: 'none',
          '::-webkit-scrollbar': {
            display: 'none'
          },
          overflowY: isBlogFormOpen ? 'hidden !important' : ''
        }}
        background={() => {
          if (pageName === MY_PROFILES) {
            return COLORS.gray
          }

          if (pageName === BLOGS) {
            if (postsTypeStatus === 'archived') {
              return FIRST_TAB_COLOR_INFO?.bgColor
            }

            if (postsTypeStatus === 'scheduled') {
              return SECOND_TAB_COLOR_INFO?.bgColor
            }

            if (postsTypeStatus === 'posted') {
              return THIRD_TAB_COLOR_INFO?.bgColor
            }
          }

          return COLORS.white
        }}
        flex="1 1 auto"
        margin="0px"
        padding="0px"
        position="relative"
      >
        {
          isAuth() && user
            ? (
              <UserSection
                pageName={pageName}
                selectedUser={selectedUser}
                setSelectedUser={setSelectedUser}
                setSocialLinksArray={setSocialLinksArray}
                socialLinksArray={socialLinksArray}
                setSocialLinkSelected={setSocialLinkSelected}
                socialLinkSelected={socialLinkSelected}
              />
            )
            : <Auth pageName={pageName} />
        }
      </Box>
    </MotionBox>
  )

  return (
    <FocusLock isDisabled={isAuth() && user}>
      <AnimatePresence>
        {
          userSectionPage.length
          && (
            <>
              <Box
                zIndex={handleOverlayZIndex}
                background="transparent"
                position="fixed"
                width="100%"
                height={userSectionPage.at(-1) === EDIT_PROFILE && isProfileChanged ? '100%' : '50px'}
                top={0}
                onClick={() => {
                  if (
                    isProfileChanged === true
                    && isOnEditProfile
                  ) {
                    setIsDiscard(true)
                    setIsClickedOutside(true)
                  } else {
                    dispatch(setCloseAllDrawers(true))
                    setTimeout(() => dispatch(setCloseAllDrawers(false)), 850)
                  }
                }}
              />

              <Box
                zIndex={isAuth() ? 1399 : 1390}
                background="transparent"
                position="fixed"
                width="100%"
                height="50px"
                bottom={0}
                onClick={() => {
                  if (
                    isProfileChanged === true
                    && isOnEditProfile
                  ) {
                    setIsDiscard(true)
                    setIsClickedOutside(true)
                  } else {
                    dispatch(setCloseAllDrawers(true))
                    setTimeout(() => dispatch(setCloseAllDrawers(false)), 850)
                  }
                }}
              />

              <DiscardChanges
                isDiscard={isDiscard}
                setIsDiscard={setIsDiscard}
                isProfileIncomplete={isProfileIncomplete}
                isClickedOutside={isClickedOutside}
                setIsClickedOutside={setIsClickedOutside}
              />
              <CropPicture
                isPictureUploaded={isPictureUploaded}
                setCloseCropModal={setCloseCropModal}
                cropFor={cropFor}
              />
            </>
          )
        }

        { userSectionPage.map((pageName) => handleContent(pageName)) }
      </AnimatePresence>
    </FocusLock>
  )
}
